import { createRouter as cr, createWebHashHistory, createWebHistory } from "vue-router";

import { ProjectDetailSections, RESERVED_WORKSPACE_PUBLIC_IDS, RouteNames, routes } from "@/routes";
import { useAuthStore } from "@/stores/auth";
import { useToastsStore } from "@/stores/toasts";

declare module "vue-router" {
  interface RouteMeta {
    requiresAuthentication: boolean;
  }
}

export function isValidProjectDetailSection(section: string): boolean {
  return Object.values(ProjectDetailSections).includes(section as ProjectDetailSections);
}

function createRouter() {
  /**
   * To handle PR deployment in the same bucket
   * we use anchor navigation if the base is not empty.
   */
  const history =
    import.meta.env.BASE_URL !== "/"
      ? createWebHashHistory(import.meta.env.BASE_URL)
      : createWebHistory(import.meta.env.BASE_URL);
  const router = cr({
    history,
    routes,
  });
  router.beforeEach(async (to) => {
    // Reject reserved workspacePublicId
    const workspacePublicId = to.params.workspacePublicId;

    if (
      typeof workspacePublicId === "string" &&
      RESERVED_WORKSPACE_PUBLIC_IDS.has(workspacePublicId)
    ) {
      return { name: RouteNames.NotFound };
    }

    // Access store inside the guard when Pinia is initialized
    const authStore = useAuthStore();

    // refresh the token if needed
    if ("error" in to.query) {
      const toastsStore = useToastsStore();
      switch (to.query["error"]) {
        case "invalid_state_exception": {
          toastsStore.addToast(
            "Something went wrong during authentication, please try again.",
            "error"
          );
          break;
        }
        default: {
          break;
        }
      }
    }
    const isUserAuthenticated = await authStore.ensureUserIsAuthenticated();
    if (to.meta.requiresAuthentication && !isUserAuthenticated) {
      return { name: RouteNames.Dispatch, query: { next: to.fullPath } };
    }

    if (authStore.currentUser === null && authStore.isAuthenticated === true) {
      await authStore.fetchMe();
    }
  });
  return router;
}

export const router = createRouter();
